/**
 * Print colors. Components are 0–1. `gray.k` is INK coverage (1 = black), so
 * gray and CMYK read the same way. The PDF backend writes each space
 * natively; previews convert naively to sRGB (good enough on screen, not a
 * color-managed proof).
 *
 * Plan linework defaults to `gray` K-only black: a rich (4-color) black on a
 * hairline misregisters on press.
 */

export type PrintColor = { space: 'gray'; k: number } | { space: 'cmyk'; c: number; m: number; y: number; k: number } | { space: 'rgb'; r: number; g: number; b: number };

export const PRINT_BLACK: PrintColor = { space: 'gray', k: 1 };
export const PRINT_WHITE: PrintColor = { space: 'gray', k: 0 };

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function printColorToRgb(color: PrintColor): { r: number; g: number; b: number } {
  switch (color.space) {
    case 'gray': {
      const v = 1 - clamp01(color.k);
      return { r: v, g: v, b: v };
    }
    case 'cmyk': {
      const k = 1 - clamp01(color.k);
      return { r: (1 - clamp01(color.c)) * k, g: (1 - clamp01(color.m)) * k, b: (1 - clamp01(color.y)) * k };
    }
    case 'rgb':
      return { r: clamp01(color.r), g: clamp01(color.g), b: clamp01(color.b) };
  }
}

/** `#rrggbb` approximation for on-screen previews. */
export function printColorToCss(color: PrintColor): string {
  const { r, g, b } = printColorToRgb(color);
  const hex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** Parses `#rgb` / `#rrggbb` into an RGB print color, or null. */
export function printColorFromHex(hex: string): PrintColor | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((d) => d + d)
          .join('')
      : match[1];
  const channel = (i: number) => parseInt(digits.slice(i * 2, i * 2 + 2), 16) / 255;
  return { space: 'rgb', r: channel(0), g: channel(1), b: channel(2) };
}
